import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List } from 'immutable';
import Select from 'react-select';
import { compose, mapProps, withState } from 'recompose';
import { first, uniq } from 'lodash';
import { Link } from 'react-router-dom';

import { peopleByIds } from '../../../entities/Person/selectors';
import { Endpoints } from '../../../lib/LeanLinkService';
import withPagination from '../../PaginatedItems/withPagination';
import CreateModal from './CreateModal';
import * as styles from './styles.css';
import * as sharedStyles from '../styles.css';
import formControl from '../formControl';
import debounceProp from '../../../lib/debounceProp';
import { Routes } from '../../../lib/links';
import { contactsByIds } from '../../../entities/Contact/selectors';
import withCustomers from '../../../containers/withCustomers';
import { withTranslation } from 'react-i18next';

export const CREATE_OPTION = 'CREATE_OPTION';

const entityToValue = person => ({
  value: person.id,
  label: `${person.firstName} ${person.lastName}`,
  roles: `${person.contacts.map(contact => `${contact.role}`).join(', ')}`,
  customersIds: person.contacts.map(contact => contact.customer),
  email: person.email || '',
});

const CustomerNames = withCustomers(({ customers }) => (
  <span>
    {customers.map(customer => <span key={customer.id}>{`, ${customer.name}`}</span>)}
  </span>
));

function Option({
  label,
  display,
  roles,
  showRoles,
  email,
  showEmail,
  customersIds,
  showCustomers,
}) {
  return (
    <span
      data-testid={`select-option-${label}`}
      className={`${styles.option} ${display ? styles.display : ''}`}
    >
      <div>
        {label}
        {showRoles && `, ${roles}`}
        {showEmail ? `<${email}>` : ''}
        {showCustomers && (
          <CustomerNames customersIds={customersIds} />
        )}
      </div>
    </span>
  );
}

Option.defaultProps = {
  roles: '',
  showRoles: false,
  display: false,
  showEmail: false,
  email: '',
  showCustomers: false,
  customersIds: [],
};

Option.propTypes = {
  label: PropTypes.string.isRequired,
  roles: PropTypes.string,
  showRoles: PropTypes.bool,
  display: PropTypes.bool, // if view-only
  showEmail: PropTypes.bool,
  email: PropTypes.string,
  showCustomers: PropTypes.bool,
  customersIds: PropTypes.arrayOf(PropTypes.string),
};

function LinkedOption(props) {
  return (
    <Link to={Routes.contacts.view({ id: props.value })}>
      <Option {...props} />
    </Link>
  );
}

LinkedOption.propTypes = {
  value: PropTypes.string.isRequired,
};

class ContactSelection extends Component {
  static propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })).isRequired,
    input: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
      ]),
      onChange: PropTypes.func.isRequired,
      onBlur: PropTypes.func,
    }).isRequired,
    disabled: PropTypes.bool,
    isEditable: PropTypes.bool,
    multi: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    setQuery: PropTypes.func.isRequired,
    refresh: PropTypes.func.isRequired,
    creatable: PropTypes.bool,
    showRoles: PropTypes.bool,
    autoselect: PropTypes.bool,
    testID: PropTypes.string,
    showEmail: PropTypes.bool.isRequired,
    showCustomers: PropTypes.bool,
    newRole: PropTypes.bool,
    customerId: PropTypes.string,
  };

  static defaultProps = {
    multi: false,
    disabled: false,
    isEditable: true,
    creatable: false,
    showRoles: false,
    autoselect: false,
    testID: 'PersonSelect',
    showEmail: false,
    showCustomers: false,
    newRole: false,
    customerId: undefined,
  };

  constructor(props) {
    super(props);
    const modal = props.input.value === CREATE_OPTION;
    this.state = {
      modal,
      autoselect: props.autoselect,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.autoselect !== nextProps.autoselect) {
      this.setState({ autoselect: nextProps.autoselect });
    }

    if (this.state.autoselect
      && this.props.isLoading
      && !nextProps.isLoading
      && (nextProps.input.value === undefined || nextProps.input.value === '')
    ) {
      if (nextProps.options.length === 1) {
        nextProps.input.onChange(first(nextProps.options).value);
      }

      // Only once
      this.setState({
        autoselect: false,
      });
    }

    if (nextProps.input.value && !nextProps.isLoading) {
      if (nextProps.multi) {
        // Note: TODO.
      } else if (!nextProps.options.some(option => option.value === nextProps.input.value)) {
        this.props.input.onChange(null);
      }
    }
  }

  handleChange = (values) => {
    if (values && values.key === CREATE_OPTION) {
      this.setState({ modal: true });
    }

    return this.props.input.onChange(this.handleValues(values));
  };

  handleValues = (value) => {
    if (value === null) {
      return value;
    }

    return (this.props.multi ? new List((value || []).map(v => v.value)) : (value || {}).value);
  };

  val = () => {
    if (!this.props.input.value) {
      return this.props.input.value;
    }

    return (this.props.multi ? this.props.input.value.toJS() : this.props.input.value);
  };

  options = () => {
    if (this.props.creatable) {
      return [
        ...this.props.options,
        {
          key: CREATE_OPTION,
          label: this.props.t('Skapa ny...'),
        },
      ];
    }
    return this.props.options;
  }

  handleBlur = () => {
    this.props.input.onBlur(this.props.input.value);
  };

  renderOption = props => (
    <Option
      {...props}
      showRoles={this.props.showRoles}
      showEmail={this.props.showEmail}
      showCustomers={this.props.showCustomers}
    />);

  render() {
    if (!this.props.isEditable) {
      if ((!this.props.input.value || this.props.input.value.size === 0)) {
        return (<i className={sharedStyles.value}>Ingen användare vald.</i>);
      }

      const values = this.props.multi ? this.props.input.value : [this.props.input.value];
      return (
        <div className={styles.optionsContainer}>
          {this.props.options
            .filter(person => values.includes(person.value))
            .map(person => (
              <LinkedOption
                display
                key={person.value}
                value={person.value}
                label={person.label}
              />
            ))}
        </div>
      );
    }

    return (
      <div data-testid={this.props.testID} className={styles.container}>
        <CreateModal
          show={this.state.modal}
          onHide={() => this.setState({ modal: false })}
          onSuccess={(person) => {
            this.props.refresh();
            this.setState({ modal: false });
            this.handleChange(entityToValue(person));
          }}
          newRole={this.props.newRole}
          customerId={this.props.customerId}
        />
        <Select
          disabled={this.props.disabled}
          placeholder={this.props.t("Namn...")}
          name="search"
          autoload={false}
          valueRenderer={this.renderOption}
          optionRenderer={this.renderOption}
          options={this.options()}
          onChange={this.handleChange}
          onInputChange={this.props.setQuery}
          onBlur={this.handleBlur}
          value={this.val()}
          className={`${sharedStyles.inputContainer} ${styles.inputContainer}`}
          isLoading={this.props.isLoading}
          filterOption={() => true}
          multi={this.props.multi}
        />
      </div>
    );
  }
}

function mapStateToProps(state, { customers, ids, input: { value } }) {
  const allPeopleIds = value !== CREATE_OPTION ? [...ids, value] : ids;
  const peopleIds = uniq(allPeopleIds).filter(v => v);
  const people = peopleByIds(state, peopleIds)
    .map(person =>
    (person.contacts ?
      person.set('contacts', contactsByIds(state, person.contacts.toJS()))
      : person.set('contacts', [])))
    .filter(person =>
      (customers ? person.contacts.some(contact => customers.includes(contact.customer)) : true),
    );

  return {
    options: people.map(entityToValue),
  };
}

export default compose(
  withTranslation(),
  formControl,
  withState('query', 'setQuery', ''),
  mapProps(
    props => ({
      ...props,
      url: Endpoints.people.list({
        name: props.query,
        customers: props.customerId,
        includeHierarchyCustomers: props.includeHierarchyCustomers,
      }),
    })),
  debounceProp('url'),
  withPagination(),
  connect(mapStateToProps),
)(ContactSelection);

